import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useMediaQuery } from "react-responsive";
import Modal from "react-responsive-modal";
import { useNavigate } from "react-router-dom";
import { deleteScrap, getAllScraps } from "../../../redux/action/scraps";
import AddScrapModal from "./AddScrapModal";

const PAGE_SIZE = 8;

const Scraps = () => {
  const { account } = useSelector((state) => state.account);
  // Scraps live on the contracts service now, not on the organization document.
  const { scraps, deletingScrap } = useSelector((state) => state.scraps);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isTablet = useMediaQuery({ query: "(max-width: 1224px)" });

  useEffect(() => {
    dispatch(getAllScraps());
  }, [dispatch]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // scrap being edited (null = create)
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);

  const canAdd = account?.permissions?.includes("add-scraps");
  const canRemove = account?.permissions?.includes("remove-scraps");

  const filteredScraps = useMemo(
    () =>
      (scraps || []).filter((item) =>
        `${item?.name}-${item?.variant}-${item?.type}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      ),
    [scraps, searchTerm]
  );

  const totalPages = Math.max(1, Math.ceil(filteredScraps.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageScraps = filteredScraps.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (scrap) => {
    setEditing(scrap);
    setModalOpen(true);
  };

  // Creating/editing a scrap is owned by the contracts service; refresh the
  // scrap list to pick up the change.
  const handleSaved = () => {
    dispatch(getAllScraps());
  };

  const handleDelete = (scrap) => {
    if (!window.confirm(`Delete "${scrap.name} - ${scrap.variant}"?`)) return;
    dispatch(deleteScrap(scrap._id));
  };

  return (
    <div>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        styles={{
          modal: {
            borderRadius: "10px",
            width: isTablet ? "94%" : "640px",
            maxWidth: "94%",
          },
        }}
      >
        <AddScrapModal
          scrap={editing}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      </Modal>

      {/* Header */}
      <div className="flex space-between align-center flex-wrap gap-1 mt-1">
        <h5 className="bold" style={{ margin: 0 }}>
          Scraps
        </h5>

        <div
          className="flex align-center gap-1 flex-wrap"
          style={{ justifyContent: "flex-end" }}
        >
          <div
            className="input-style flex align-center small-text"
            style={{
              margin: 0,
              color: "#98A2B3",
              background: "#fff",
              border: "1px solid #E4E7EC",
              borderRadius: 10,
              padding: "0 12px",
              height: 44,
              minWidth: 240,
            }}
          >
            <span className="material-symbols-outlined mr-1">search</span>
            <input
              style={{ margin: 0, caretColor: "black" }}
              placeholder="Search"
              className="hide-focus-underline browser-default"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />
          </div>

          <button
            className="btn-small white purple-text flex align-center"
            style={{ border: "1px solid #6F2DA8", height: 44, gap: 6 }}
            disabled={!canAdd}
            onClick={() => navigate("/organization/scraps/bulk-upload")}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
              add
            </span>
            Add Bulk Scraps
          </button>

          <button
            className="btn-small flex align-center"
            style={{
              background: "var(--primary-color)",
              border: "none",
              height: 44,
              gap: 6,
            }}
            disabled={!canAdd}
            onClick={openCreate}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
              add
            </span>
            Add More Scraps
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded white mt-1" style={{ overflow: "hidden" }}>
        <table className="striped responsive-table dashboardtablev2">
          <thead>
            <tr className="table-header-row">
              <th>Scrap Name</th>
              <th>Code</th>
              <th>Category</th>
              <th>Type / Grade</th>
              <th>Unit</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageScraps.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <div className="flex align-center justify-center column p-1">
                    <img
                      className="mb-1"
                      style={{ width: 64, height: 64 }}
                      src="../images/no-data.svg"
                      alt="No Data"
                    />
                    <label className="grey-text">
                      {scraps?.length
                        ? "No matching scraps found"
                        : "No scraps yet. Add your first scrap."}
                    </label>
                  </div>
                </td>
              </tr>
            ) : (
              pageScraps.map((item) => (
                <tr key={item._id}>
                  <td
                    className="cercle-purple-text"
                    style={{ fontWeight: 700 }}
                  >
                    {item?.name}
                  </td>
                  <td>
                    {item?.articleCode ? (
                      <span className="trader-pill muted">
                        {item.articleCode}
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{item?.type || "-"}</td>
                  <td>{item?.variant || "-"}</td>
                  <td>
                    <span className="trader-pill info">
                      {(item?.uom || "").toUpperCase() || "-"}
                    </span>
                  </td>
                  <td>
                    <div
                      className="flex align-center"
                      style={{ justifyContent: "flex-end", gap: 8 }}
                    >
                      {canAdd && (
                        <button
                          className="btn-flat scrap-action-btn"
                          title="Edit"
                          onClick={() => openEdit(item)}
                        >
                          <span
                            className="material-symbols-outlined cercle-purple-text"
                            style={{ fontSize: 20 }}
                          >
                            edit
                          </span>
                        </button>
                      )}
                      {canRemove && (
                        <button
                          className="btn-flat scrap-action-btn"
                          title="Delete"
                          disabled={deletingScrap}
                          onClick={() => handleDelete(item)}
                        >
                          <span
                            className="material-symbols-outlined red-text"
                            style={{ fontSize: 20 }}
                          >
                            delete
                          </span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Footer / pagination */}
        <div
          className="flex align-center space-between p-1"
          style={{ borderTop: "1px solid #F2F4F7" }}
        >
          <span className="grey-text small-text">
            {filteredScraps.length} scrap
            {filteredScraps.length === 1 ? "" : "s"}
          </span>

          {totalPages > 1 && (
            <div className="flex align-center gap-1">
              <button
                className="btn-flat scrap-action-btn"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <span className="small-text">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="btn-flat scrap-action-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Scraps;
